import Swiper from 'swiper';
import { Navigation, Keyboard, A11y } from 'swiper/modules';
import gsap from "gsap";

export default function initItemCards() {
	const blocks = document.querySelectorAll('.item-cards');
	const GAP = 16;
	const DUR = 0.4;
	const EASE = "power2.out";

	// Save and restore aria-labels that Swiper's A11y module overwrites
	const preserveAriaLabels = (el) => el ? el.getAttribute('aria-label') : null;
	const restoreAriaLabel = (el, label) => { if (el && label) el.setAttribute('aria-label', label); };

	// Prevent off-screen slides from receiving keyboard focus
	const updateSlideFocus = (slides, activeIndex) => {
		slides.forEach((slide, i) => {
			const focusables = slide.querySelectorAll('a, button, input, [tabindex]');
			focusables.forEach(el => {
				el.setAttribute('tabindex', i === activeIndex ? '0' : '-1');
			});
		});
	};

	blocks.forEach(block => {
		if (block.dataset.initialized) return;
		block.dataset.initialized = 'true';

		if (block.classList.contains('is-mask')) {
			const viewport = block.querySelector(".swiper");
			const track = block.querySelector(".swiper-wrapper");
			const slides = Array.from(block.querySelectorAll(".swiper-slide"));
			const prevBtn = block.querySelector(".swiper-button-prev");
			const nextBtn = block.querySelector(".swiper-button-next");

			if (!viewport || !track || !slides.length) return;

			let index = 0;
			let swiperInstance = null;

			// Dimensions state
			let smallWidth = 314;
			let largeWidth = 598;
			let smallHeight = 337;
			let largeHeight = 656;

			// Initialize Index
			const initialActive = slides.findIndex(s => s.classList.contains("swiper-slide-active"));
			if (initialActive > -1) index = initialActive;
			else {
				index = 0;
				slides[0].classList.add("swiper-slide-active");
			}

			const updateMetrics = () => {
				const isXl = window.matchMedia("(min-width: 1200px) and (max-width: 1400px)").matches;
				largeWidth = isXl ? 540 : 598;
				smallWidth = 314;
				smallHeight = 337;
				largeHeight = 656;
			};

			const animState = { progress: 0 };

			const alignTo = (newIndex, animate = true) => {
				const oldIndex = index;
				newIndex = Math.max(0, Math.min(newIndex, slides.length - 1));

				if (newIndex === oldIndex && animate) return;
				index = newIndex;

				const oldSlide = slides[oldIndex];
				const newSlide = slides[newIndex];

				const startX = -(oldIndex * (smallWidth + GAP));
				const endX = -(newIndex * (smallWidth + GAP));

				const oldStartW = largeWidth;
				const oldEndW = smallWidth;
				const oldStartH = largeHeight;
				const oldEndH = smallHeight;

				const newStartW = smallWidth;
				const newEndW = largeWidth;
				const newStartH = smallHeight;
				const newEndH = largeHeight;

				slides.forEach(s => s.classList.remove("swiper-slide-active"));
				newSlide.classList.add("swiper-slide-active");
				updateSlideFocus(slides, newIndex);

				prevBtn?.classList.toggle("swiper-button-disabled", index === 0);
				nextBtn?.classList.toggle("swiper-button-disabled", index === slides.length - 1);

				if (animate) {
					animState.progress = 0;
					gsap.killTweensOf(animState);
					gsap.to(animState, {
						progress: 1,
						duration: DUR,
						ease: EASE,
						onUpdate: () => {
							const p = animState.progress;
							const curX = startX + (endX - startX) * p;
							track.style.transform = `translate3d(${curX}px, 0, 0)`;

							if (oldIndex !== newIndex) {
								if (oldSlide) {
									const curOldW = oldStartW + (oldEndW - oldStartW) * p;
									const curOldH = oldStartH + (oldEndH - oldStartH) * p;
									oldSlide.style.flexBasis = `${curOldW}px`;
									oldSlide.style.width = `${curOldW}px`;
									oldSlide.style.height = `${curOldH}px`;
								}
								if (newSlide) {
									const curNewW = newStartW + (newEndW - newStartW) * p;
									const curNewH = newStartH + (newEndH - newStartH) * p;
									newSlide.style.flexBasis = `${curNewW}px`;
									newSlide.style.width = `${curNewW}px`;
									newSlide.style.height = `${curNewH}px`;
								}
							}
						},
						onComplete: () => {
							track.style.transform = `translate3d(${endX}px, 0, 0)`;
							if (oldIndex !== newIndex) {
								if (oldSlide) {
									oldSlide.style.flexBasis = `${oldEndW}px`;
									oldSlide.style.width = `${oldEndW}px`;
									oldSlide.style.height = `${oldEndH}px`;
								}
								if (newSlide) {
									newSlide.style.flexBasis = `${newEndW}px`;
									newSlide.style.width = `${newEndW}px`;
									newSlide.style.height = `${newEndH}px`;
								}
							}
						}
					});
				} else {
					track.style.transform = `translate3d(${endX}px, 0, 0)`;
					slides.forEach(s => {
						s.style.flexBasis = '';
						s.style.width = '';
						s.style.height = '';
					});
				}
			};

			const isMobile = () => window.matchMedia("(max-width: 991px)").matches;

			const enableMobileSwiper = () => {
				if (swiperInstance) return;
				gsap.set(track, { clearProps: "all" });
				slides.forEach(s => s.style.cssText = '');

				const prevLabel = preserveAriaLabels(prevBtn);
				const nextLabel = preserveAriaLabels(nextBtn);

				swiperInstance = new Swiper(viewport, {
					modules: [Navigation, Keyboard, A11y],
					keyboard: { enabled: true },
					slidesPerView: "auto",
					spaceBetween: GAP,
					loop: false,
					initialSlide: index,
					navigation: { nextEl: nextBtn, prevEl: prevBtn },
					slidesOffsetAfter: 200,
					threshold: 10,
					touchStartPreventDefault: false,
					watchSlidesProgress: true,
					on: {
						slideChange: (sw) => { index = sw.activeIndex; }
					}
				});

				restoreAriaLabel(prevBtn, prevLabel);
				restoreAriaLabel(nextBtn, nextLabel);
			};

			const disableMobileSwiper = () => {
				if (swiperInstance) {
					index = swiperInstance.activeIndex;
					swiperInstance.destroy(true, true);
					swiperInstance = null;
				}
			};

			const applyMode = () => {
				updateMetrics();
				if (isMobile()) {
					enableMobileSwiper();
				} else {
					disableMobileSwiper();
					alignTo(index, false);
					updateSlideFocus(slides, index);
				}
			};

			let resizeTimer;
			const onResize = () => {
				clearTimeout(resizeTimer);
				resizeTimer = setTimeout(applyMode, 100);
			};

			window.addEventListener('resize', onResize);
			// Trigger on init
			applyMode();

			prevBtn?.addEventListener('click', (e) => {
				if (isMobile()) return;
				e.preventDefault();
				if (index > 0) alignTo(index - 1);
			});

			nextBtn?.addEventListener('click', (e) => {
				if (isMobile()) return;
				e.preventDefault();
				if (index < slides.length - 1) alignTo(index + 1);
			});

		} else {
			// Standard Swiper
			const stdPrevBtn = block.querySelector('.swiper-button-prev');
			const stdNextBtn = block.querySelector('.swiper-button-next');
			const stdPrevLabel = preserveAriaLabels(stdPrevBtn);
			const stdNextLabel = preserveAriaLabels(stdNextBtn);

			new Swiper(block.querySelector('.swiper'), {
				modules: [Navigation, Keyboard, A11y],
				keyboard: { enabled: true },
				slidesPerView: "auto",
				spaceBetween: GAP,
				loop: false,
				threshold: 10,
				touchStartPreventDefault: false,
				watchSlidesProgress: true,
				navigation: {
					nextEl: stdNextBtn,
					prevEl: stdPrevBtn,
				}
			});

			restoreAriaLabel(stdPrevBtn, stdPrevLabel);
			restoreAriaLabel(stdNextBtn, stdNextLabel);
		}
	});
}
